import React, { useState } from "react";
import { Input } from "../ui/input";
import { Plus, Trash2, Upload, Film, Link as LinkIcon, ChevronUp, ChevronDown } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import { uploadFile, fileUrl } from "../../lib/api";
import { parseEmbed } from "../VideoPlayer";

const inputClass = "bg-[#F6F1E8] border border-[#1A1714]/15 rounded-sm focus-visible:ring-1 focus-visible:ring-[#1A1714]";

const arrSwap = (arr, i, j) => {
  if (j < 0 || j >= arr.length) return arr;
  const copy = [...arr];
  [copy[i], copy[j]] = [copy[j], copy[i]];
  return copy;
};

/**
 * Reusable video manager.
 * `videos` is an array of { kind: "upload" | "embed", url, title?, caption? }
 */
export default function VideosEditor({ videos = [], onChange, label = "Videos", helper }) {
  const { toast } = useToast();
  const [embedUrl, setEmbedUrl] = useState("");
  const [embedTitle, setEmbedTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const addEmbed = () => {
    const url = embedUrl.trim();
    if (!url) return;
    const parsed = parseEmbed(url);
    if (parsed.platform === "unknown" && !/^https?:\/\//i.test(url)) {
      toast({ title: "Invalid URL", description: "Paste a YouTube, Vimeo or TikTok link.", variant: "destructive" });
      return;
    }
    onChange([...(videos || []), { kind: "embed", url, title: embedTitle.trim() }]);
    setEmbedUrl("");
    setEmbedTitle("");
  };

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    setProgress(0);
    try {
      const res = await uploadFile(f, setProgress);
      onChange([...(videos || []), { kind: "upload", url: res.url, filename: res.filename, title: f.name.replace(/\.[^.]+$/, "") }]);
      toast({ title: "Video uploaded", description: f.name });
    } catch (err) {
      toast({
        title: "Upload failed",
        description: err?.response?.data?.detail || err.message || "Try again.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
      setProgress(0);
      e.target.value = "";
    }
  };

  return (
    <div>
      <label className="text-[10px] tracking-[0.22em] uppercase text-[#1A1714]/55 flex items-center gap-2">
        <Film size={12} /> {label}
      </label>
      {helper && <p className="text-xs text-[#1A1714]/55 mt-1">{helper}</p>}

      {/* Existing videos list */}
      <div className="mt-3 space-y-2">
        {(videos || []).map((v, i) => (
          <div key={i} className="flex items-center gap-3 bg-[#EFE8DA] border border-[#1A1714]/15 rounded-sm px-3 py-2">
            <span className={`text-[10px] tracking-[0.18em] uppercase px-2 py-1 rounded ${v.kind === "upload" ? "bg-[#1A1714] text-[#F6F1E8]" : "bg-[#B5523A]/15 text-[#B5523A]"}`}>
              {v.kind === "upload" ? "Upload" : (parseEmbed(v.url).platform || "Embed")}
            </span>
            <div className="flex-1 min-w-0">
              <Input
                className={`${inputClass} h-8 text-sm`}
                placeholder="Optional title shown below the player"
                value={v.title || ""}
                onChange={(e) => onChange(videos.map((x, k) => (k === i ? { ...x, title: e.target.value } : x)))}
              />
              {v.kind === "upload" ? (
                <a href={fileUrl(v.url)} target="_blank" rel="noreferrer" className="text-[11px] text-[#1A1714]/60 truncate block mt-1">
                  {v.filename || v.url}
                </a>
              ) : (
                <a href={v.url} target="_blank" rel="noreferrer" className="text-[11px] text-[#1A1714]/60 truncate block mt-1 hover:underline">
                  {v.url}
                </a>
              )}
            </div>
            <div className="flex items-center gap-1">
              {i > 0 && (
                <button type="button" onClick={() => onChange(arrSwap(videos, i, i - 1))} className="p-1.5 rounded hover:bg-[#1A1714]/10" title="Move up"><ChevronUp size={14} /></button>
              )}
              {i < videos.length - 1 && (
                <button type="button" onClick={() => onChange(arrSwap(videos, i, i + 1))} className="p-1.5 rounded hover:bg-[#1A1714]/10" title="Move down"><ChevronDown size={14} /></button>
              )}
              <button type="button" onClick={() => onChange(videos.filter((_, k) => k !== i))} className="p-1.5 rounded hover:bg-[#B5523A]/15 text-[#B5523A]" title="Remove">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add new */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-[#EFE8DA] border border-dashed border-[#1A1714]/25 rounded-sm p-4">
          <div className="text-[11px] tracking-[0.2em] uppercase text-[#1A1714]/60 flex items-center gap-2">
            <Upload size={12} /> Upload video file
          </div>
          <p className="text-xs text-[#1A1714]/55 mt-1">.mp4, .mov, .webm — up to 500 MB.</p>
          <label className="mt-3 inline-flex items-center gap-2 cursor-pointer text-[12px] tracking-[0.18em] uppercase border border-[#1A1714]/25 px-3 py-2 rounded-sm hover:bg-[#1A1714] hover:text-[#F6F1E8] transition-colors">
            {busy ? `Uploading… ${progress}%` : "Choose file"}
            <input type="file" accept="video/*,.mp4,.mov,.webm,.m4v" onChange={handleUpload} disabled={busy} className="hidden" />
          </label>
        </div>

        <div className="bg-[#EFE8DA] border border-dashed border-[#1A1714]/25 rounded-sm p-4">
          <div className="text-[11px] tracking-[0.2em] uppercase text-[#1A1714]/60 flex items-center gap-2">
            <LinkIcon size={12} /> Or paste a link
          </div>
          <p className="text-xs text-[#1A1714]/55 mt-1">YouTube, Vimeo or TikTok URL.</p>
          <Input
            className={`${inputClass} mt-3`}
            placeholder="https://youtu.be/..."
            value={embedUrl}
            onChange={(e) => setEmbedUrl(e.target.value)}
          />
          <Input
            className={`${inputClass} mt-2`}
            placeholder="Optional title"
            value={embedTitle}
            onChange={(e) => setEmbedTitle(e.target.value)}
          />
          <button
            type="button"
            onClick={addEmbed}
            disabled={!embedUrl.trim()}
            className="mt-3 inline-flex items-center gap-2 bg-[#1A1714] text-[#F6F1E8] px-4 py-2 rounded-sm text-[12px] tracking-[0.18em] uppercase hover:bg-[#2c2620] disabled:opacity-50"
          >
            <Plus size={14} /> Add video
          </button>
        </div>
      </div>
    </div>
  );
}
